import { useParams } from "react-router-dom";
import { Award } from "lucide-react";
import Seo from "@/components/site/Seo";
import { Crest, EmptyState, StatusPill } from "@/components/site/kit";
import { indexBy } from "@/components/site/cards";
import useFetch from "@/hooks/useFetch";
import { fileUrl, fmtDate } from "@/lib/api";
import NotFound from "./NotFound";

const Innings = ({ inn, team }) => (
  <div data-testid={`innings-${inn.team_id}`} className="overflow-hidden rounded-md border border-white/10">
    <div className="flex items-center justify-between bg-[#112240] p-4">
      <h3 className="font-display text-xl uppercase">{team?.name || "Team"} Innings</h3>
      <p className="font-score text-2xl font-bold text-[#00E5FF]">{inn.runs}/{inn.wickets} <span className="text-sm text-slate-400">({inn.overs} ov)</span></p>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead className="text-[11px] uppercase tracking-widest text-slate-400"><tr>{["Batter", "", "R", "B", "4s", "6s", "SR"].map((h, i) => <th key={i} className="px-4 py-3">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-white/5">
          {inn.batting.map((b, i) => (
            <tr key={i}><td className="px-4 py-2.5 font-semibold">{b.player}</td><td className="px-4 py-2.5 text-xs text-slate-400">{b.how_out || "not out"}</td>
              <td className="px-4 py-2.5 font-score font-bold">{b.runs}</td><td className="px-4 py-2.5 font-score">{b.balls}</td><td className="px-4 py-2.5 font-score">{b.fours}</td><td className="px-4 py-2.5 font-score">{b.sixes}</td>
              <td className="px-4 py-2.5 font-score">{b.balls ? ((b.runs / b.balls) * 100).toFixed(1) : "-"}</td></tr>
          ))}
        </tbody>
      </table>
      <p className="border-t border-white/5 px-4 py-2 text-xs text-slate-400">Extras: {inn.extras}</p>
      <table className="w-full min-w-[560px] border-t border-white/10 text-left text-sm">
        <thead className="text-[11px] uppercase tracking-widest text-slate-400"><tr>{["Bowler", "O", "M", "R", "W", "Econ"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-white/5">
          {inn.bowling.map((b, i) => (
            <tr key={i}><td className="px-4 py-2.5 font-semibold">{b.player}</td><td className="px-4 py-2.5 font-score">{b.overs}</td><td className="px-4 py-2.5 font-score">{b.maidens}</td>
              <td className="px-4 py-2.5 font-score">{b.runs}</td><td className="px-4 py-2.5 font-score font-bold">{b.wickets}</td><td className="px-4 py-2.5 font-score">{b.overs ? (b.runs / b.overs).toFixed(2) : "-"}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default function MatchCentre() {
  const { id } = useParams();
  const { data: m, loading, error } = useFetch(`/matches/${id}`);
  const { data: teams } = useFetch("/teams", []);
  const { data: venues } = useFetch("/venues", []);
  if (error) return <NotFound />;
  if (loading || !m) return <div className="min-h-screen" />;
  const tMap = indexBy(teams);
  const venue = indexBy(venues)[m.venue_id];
  const side = (tid) => {
    const t = tMap[tid];
    const inn = m.innings?.find((i) => i.team_id === tid);
    return (
      <div className="flex flex-col items-center gap-3 text-center">
        <Crest number={t?.number || 0} color={t?.primary_color} logo={fileUrl(t?.logo)} name={t?.name} />
        <p className="font-display text-2xl uppercase">{t?.name || "TBA"}</p>
        <p className="font-score text-4xl font-bold text-[#00E5FF]">{inn ? `${inn.runs}/${inn.wickets}` : "–"}</p>
        {inn && <p className="font-score text-xs text-slate-400">{inn.overs} overs</p>}
      </div>
    );
  };
  return (
    <>
      <Seo title={`Match ${m.number}`} description={`Match centre for Match ${m.number} of the Telangana–Andhra T10 League.`} />
      <section data-testid="match-centre" className="grain relative border-b border-white/10 pb-14 pt-36">
        <div className="stripe-bg absolute inset-0" />
        <div className="container-x relative">
          <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
            <span className="font-score font-bold">Match {m.number}{m.stage ? ` · ${m.stage}` : ""}</span>
            <StatusPill tone={m.status === "completed" ? "ok" : m.status === "live" ? "soon" : "mute"}>{m.status}</StatusPill>
            <span>{fmtDate(m.date)} {m.time}</span><span>· {venue?.venue_name || venue?.city || "Venue TBA"}</span>
          </div>
          <div className="mt-10 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
            {side(m.team_a_id)}<span className="font-display text-2xl text-slate-500">VS</span>{side(m.team_b_id)}
          </div>
          {m.result && <p data-testid="match-result" className="mt-10 text-center text-lg font-bold text-[#FF8a65]">{m.result}</p>}
          {m.toss && <p className="mt-2 text-center text-sm text-slate-400">Toss: {m.toss}</p>}
          {m.potm && <p data-testid="match-potm" className="mt-4 flex items-center justify-center gap-2 text-sm"><Award className="h-4 w-4 text-[#FACC15]" /> Player of the match: <b>{m.potm}</b></p>}
        </div>
      </section>
      <section className="container-x space-y-8 py-14">
        {m.innings?.length ? m.innings.map((inn, i) => <Innings key={i} inn={inn} team={tMap[inn.team_id]} />)
          : <EmptyState testid="scorecard-empty" title="Scorecard not available yet">The scorecard will appear here once the match is played. Live scoring will be connected when an official scoring partner is confirmed.</EmptyState>}
      </section>
    </>
  );
}
